import { BUTTON_STYLE_CONFIG } from '@frontend/legacy-components/src/components/forms/button/buttonStyleConfig';
import {
  getBaseStyleObjectFromStyleConfig,
  getStyleObjectFromStyleInterpolation,
  type StyleFunctionProps,
  type StyleObjectMulti,
} from '@frontend/legacy-components/src/theme/styleConfigHelpers';

import { MENU_PARTS } from './menuParts';
import type {
  MenuColorScheme,
  MenuPart,
  MenuSize,
  MenuStyleConfig,
  MenuVariant,
} from './types';

const baseStyle: MenuStyleConfig['baseStyle'] = ({ theme, colorMode }) => {
  const baseButtonStyles = getBaseStyleObjectFromStyleConfig(
    BUTTON_STYLE_CONFIG,
    {
      theme,
      variant: 'secondary',
      colorScheme: 'default',
      colorMode,
    },
  );

  const secondaryVariantButtonStyles = getStyleObjectFromStyleInterpolation(
    BUTTON_STYLE_CONFIG.variants.secondary,
    {
      theme,
      variant: 'secondary',
      colorScheme: 'default',
      colorMode,
    },
  );

  const secondaryButtonStyles = {
    ...baseButtonStyles,
    ...secondaryVariantButtonStyles,
  };

  return {
    button: {
      ...secondaryButtonStyles,
      transitionProperty: 'common',
      transitionDuration: 'normal',
    },
    list: {
      bg: 'page.surface',
      boxShadow: 'popover',
      color: 'inherit',
      pb: 1,
      pt: 1,
      minW: '120',
      zIndex: 'dropdown',
      borderRadius: 'md',
      border: '1px solid',
      borderColor: 'separator.border',
      overflow: 'auto',
      _firstChild: {
        borderTop: '0px transparent',
      },
    },
    item: {
      h: 4,
      px: 2,
      py: 0.5,
      fontSize: 'md',
      lineHeight: 'md',
      fontWeight: 'default',
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'flex-start',
      alignItems: 'flex-start',
      transitionProperty: 'background',
      transitionDuration: 'ultra-fast',
      transitionTimingFunction: 'ease-in',
      cursor: 'unset',
      color: 'unset',
      _hover: {
        color: 'unset',
      },
      _focus: {
        bg: 'interaction.surfaceHover',
      },
      _active: {
        bg: 'interaction.surfaceActive',
      },
      _expanded: {
        bg: 'interaction.surfaceActive',
      },
      _disabled: {
        color: 'interaction.disabled',
        bg: 'page.surface',
        cursor: 'not-allowed',
      },
    },
    groupTitle: {
      mx: 2,
      mb: 0.5,
      mt: 0.5,
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'flex-end',
      fontWeight: 'heavy',
      fontSize: 'sm',
      lineHeight: 'sm',
      color: 'feature.secondary',
      textTransform: 'uppercase',
      cursor: 'default',
    },
    command: {
      color: 'feature.secondary',
    },
    divider: {
      border: 0,
      borderBottom: '1px solid',
      borderColor: 'separator.divider',
      mt: 1,
      mb: 1,
    },
  };
};

const getBaseSizeStyles = (
  size: MenuSize,
  { theme, colorMode }: StyleFunctionProps<MenuVariant, MenuColorScheme>,
): StyleObjectMulti<MenuPart> => {
  const buttonStyles = getStyleObjectFromStyleInterpolation(
    BUTTON_STYLE_CONFIG.sizes[size],
    {
      theme,
      variant: 'secondary',
      colorScheme: 'default',
      colorMode,
    },
  );

  return {
    button: buttonStyles,
  };
};

const SIZES: MenuStyleConfig['sizes'] = {
  sm: (styleFunctionProps) => {
    const base = getBaseSizeStyles('sm', styleFunctionProps);

    return {
      ...base,
      button: {
        ...base.button,
        px: 1,
        h: 3,
      },
    };
  },
  md: (styleFunctionProps) => {
    const base = getBaseSizeStyles('md', styleFunctionProps);

    return {
      ...base,
      button: {
        ...base.button,
        px: 1.5,
        h: 4,
      },
    };
  },
  lg: (styleFunctionProps) => {
    const base = getBaseSizeStyles('lg', styleFunctionProps);

    return {
      ...base,
      button: {
        ...base.button,
        px: 2,
        h: 5,
      },
    };
  },
};

export const MENU_STYLE_CONFIG: MenuStyleConfig = {
  parts: MENU_PARTS,
  baseStyle,
  sizes: SIZES,
};
